import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { StyledTextField, StyledSelect } from './StyledFormComponents';

declare global {
  interface Window {
    turnstile: any;
  }
}

const categoryOptions = [
  'Technical Issue',
  'Account Question',
  'Billing',
  'Feature Request',
  'Report Bug',
  'General Inquiry',
  'Other'
];

const ContactSupportForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: '',
  });

  // Initialize Turnstile verification
  useEffect(() => {
    let attemptInterval: ReturnType<typeof setTimeout>;

    const attemptRenderTurnstile = () => {
      if (window.turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        try {
          const widgetId = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: '0x4AAAAAABfgu43iKR6cfJY3',
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken('');
            },
            'error-callback': () => {
              setTurnstileToken('');
              setError('Verification challenge failed. Please try refreshing the page.');
            },
          });
          turnstileWidgetIdRef.current = widgetId;
          clearInterval(attemptInterval);
        } catch (renderError) {
          console.error('Turnstile render error:', renderError);
          setError('Could not load verification challenge. Please refresh.');
        }
      }
    };

    attemptRenderTurnstile();
    attemptInterval = setInterval(attemptRenderTurnstile, 1000);

    return () => {
      clearInterval(attemptInterval);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!turnstileToken) {
      setError('Please complete the human verification step.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          cfTurnstileToken: turnstileToken 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong.');
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, mb: 2 }}>
          ✅ Support Request Submitted
        </Typography>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', opacity: 0.9 }}>
          Thank you for contacting us! We've received your message and will respond via email shortly.
        </Typography>
      </Box>
    );
  }

  const isButtonDisabled = loading || !turnstileToken;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, opacity: 0.8, fontFamily: '"Poppins", sans-serif' }}>
        Need help? Fill out the form below and we'll get back to you as soon as possible.
      </Typography>

      <StyledTextField 
        label="Your Name" 
        required 
        value={form.name} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)} 
      />
      
      <StyledTextField 
        label="Your Email" 
        required 
        type="email" 
        value={form.email} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)} 
      />

      <StyledSelect
        label="Category (optional)"
        value={form.category}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('category', e.target.value)}
        options={categoryOptions}
      />

      <StyledTextField 
        label="Subject" 
        placeholder="Brief description of your issue"
        value={form.subject} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('subject', e.target.value)} 
      />

      <StyledTextField 
        label="Message" 
        required
        multiline 
        rows={5}
        placeholder="Please provide as much detail as possible..."
        value={form.message} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('message', e.target.value)} 
      />

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <div ref={turnstileContainerRef} />
      </Box>

      {error && <Typography color="error" sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem' }}>{error}</Typography>}

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isButtonDisabled}
        sx={{ 
          mt: 1, 
          py: 1.5, 
          fontFamily: '"Poppins", sans-serif', 
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '0.95rem',
          boxShadow: '0 4px 12px rgba(255, 0, 84, 0.3)',
          background: 'linear-gradient(45deg, #FF0054, #662d91)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(255, 0, 84, 0.4)',
          }
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Message'}
      </Button>
    </Box>
  );
};

export default ContactSupportForm;






